import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { ImagePlus, LayoutGrid, List, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { SLIDER_AUDIENCES, SliderAudience } from '@/services/school.api';
import { OrgSlidersApi, OrgSliderRow } from '@/services/org-sliders.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { Pagination } from '@/components/ui/Pagination';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/cn';

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

const AUDIENCE_LABEL: Record<string, string> = { student: 'Student', teacher: 'Teacher', all: 'All' };
const AUDIENCE_TONE: Record<string, 'blue' | 'indigo' | 'slate'> = { student: 'blue', teacher: 'indigo', all: 'slate' };

/**
 * Org tier — Sliders across every school in scope, flat by default (no
 * "pick a school first" step) — matches real legacy's own list view, which
 * has no school picker or school column at all (confirmed directly
 * against the real SliderController/view). The school filter below
 * narrows the list; it's optional, not a gate. See the backend's
 * sliders.service.ts "Org tier" doc comment for the fan-out mechanics
 * behind this (our schema-per-tenant architecture can't run one unscoped
 * query the way real legacy's single flat table does).
 */
export function OrgSlidersPage() {
  const qc = useQueryClient();
  const [filterSchoolId, setFilterSchoolId] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<OrgSliderRow | null>(null);
  const [deleting, setDeleting] = useState<OrgSliderRow | null>(null);

  const { data: schools = [] } = useQuery({ queryKey: ['org-sliders-schools'], queryFn: OrgSlidersApi.schools });

  const { data, isFetching } = useQuery({
    queryKey: ['org-sliders', filterSchoolId, page, limit],
    queryFn: () => OrgSlidersApi.list(filterSchoolId || undefined, { page, limit }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.items ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['org-sliders'] });

  const remove = useMutation({
    mutationFn: (row: OrgSliderRow) => OrgSlidersApi.remove(row.id, row.schoolId),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
  });

  return (
    <>
      <PageHeader
        title="Sliders"
        description="Banner images shown on the student, parent, and teacher app home screens — every school in scope, flat."
        actions={
          <button className="btn-primary flex items-center gap-1.5" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Create Slider
          </button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Select
          className="w-64"
          value={filterSchoolId}
          onChange={(e) => {
            setFilterSchoolId(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>

        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
          <button
            type="button"
            title="Grid view"
            onClick={() => setView('grid')}
            className={cn('rounded p-1.5', view === 'grid' ? 'bg-brand-50 text-brand-700' : 'text-slate-400 hover:text-slate-600')}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="List view"
            onClick={() => setView('list')}
            className={cn('rounded p-1.5', view === 'list' ? 'bg-brand-50 text-brand-700' : 'text-slate-400 hover:text-slate-600')}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isFetching ? (
        <div className="py-12 text-center text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 py-12 text-center text-sm text-slate-400">No sliders yet.</div>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => (
            <div key={r.id} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="aspect-[16/9] w-full bg-slate-100">
                <img src={r.imageUrl} alt="" className="h-full w-full object-cover" />
              </div>
              <div className="p-3">
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <Badge tone={AUDIENCE_TONE[r.forSlider] ?? 'slate'}>{AUDIENCE_LABEL[r.forSlider] ?? '(legacy — unset)'}</Badge>
                  <span className="text-xs text-slate-400">{formatDate(r.createdAt)}</span>
                </div>
                <div className="mb-1 truncate text-xs font-medium text-slate-600">{r.schoolName}</div>
                {r.link ? (
                  <a href={r.link} target="_blank" rel="noreferrer" className="block truncate text-xs text-brand-600 hover:underline">
                    {r.link}
                  </a>
                ) : (
                  <span className="block truncate text-xs text-slate-400">No link</span>
                )}
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    title="Edit"
                    className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
                    onClick={() => setEditing(r)}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    title="Delete"
                    className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
                    onClick={() => setDeleting(r)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">No.</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Image</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Link</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">For</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Created</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-slate-500">{i + 1}</td>
                  <td className="px-4 py-2.5">
                    <img src={r.imageUrl} alt="" className="h-10 w-16 rounded object-cover" />
                  </td>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{r.schoolName}</td>
                  <td className="px-4 py-2.5">
                    {r.link ? (
                      <a href={r.link} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">
                        {r.link}
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge tone={AUDIENCE_TONE[r.forSlider] ?? 'slate'}>{AUDIENCE_LABEL[r.forSlider] ?? '(legacy — unset)'}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">{formatDate(r.createdAt)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        title="Edit"
                        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
                        onClick={() => setEditing(r)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
                        onClick={() => setDeleting(r)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isFetching && rows.length > 0 && (
        <Pagination
          page={page}
          totalPages={data?.totalPages ?? 1}
          total={data?.total ?? 0}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="Create Slider" size="md">
        <SliderForm schools={schools} defaultSchoolId={filterSchoolId} onDone={() => { setCreating(false); invalidate(); }} />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Slider" size="md">
        {editing && <SliderForm schools={schools} slider={editing} onDone={() => { setEditing(null); invalidate(); }} />}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting)}
        title="Delete slider?"
        message="This will permanently remove this slider and its image. This cannot be undone."
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}

function SliderForm({
  schools,
  slider,
  defaultSchoolId,
  onDone,
}: {
  schools: { id: string; name: string }[];
  slider?: OrgSliderRow;
  defaultSchoolId?: string;
  onDone: () => void;
}) {
  const isEdit = !!slider;
  const [schoolId, setSchoolId] = useState(slider?.schoolId ?? defaultSchoolId ?? '');
  const [imageUrl, setImageUrl] = useState(slider?.imageUrl ?? '');
  const [link, setLink] = useState(slider?.link ?? '');
  const [forSlider, setForSlider] = useState<SliderAudience | ''>((slider?.forSlider as SliderAudience) || '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  const save = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return OrgSlidersApi.update(slider!.id, { schoolId, imageUrl: imageUrl || undefined, link, forSlider: forSlider || undefined });
      }
      return OrgSlidersApi.create({ schoolId, imageUrl, link: link || undefined, forSlider: forSlider as SliderAudience });
    },
    onSuccess: onDone,
  });

  async function handleFilePick(file: File) {
    if (!schoolId) {
      setUploadError('Pick a school first');
      return;
    }
    setUploading(true);
    setUploadError(undefined);
    try {
      const res = await OrgSlidersApi.upload(schoolId, file);
      setImageUrl(res.url);
    } catch (e) {
      setUploadError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  const valid = !!schoolId && !!imageUrl && !!forSlider;

  return (
    <div className="space-y-4">
      <Field label="School" required>
        <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)} disabled={isEdit}>
          <option value="">— Select a school —</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>

      <Field label="Image" required>
        {imageUrl ? (
          <div className="relative">
            <img src={imageUrl} alt="" className="aspect-[16/9] w-full rounded-md border border-slate-200 object-cover" />
            <label className="absolute inset-x-0 bottom-0 flex cursor-pointer items-center justify-center gap-1.5 rounded-b-md bg-black/50 py-1.5 text-xs text-white hover:bg-black/60">
              <ImagePlus className="h-3.5 w-3.5" /> Replace image
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleFilePick(e.target.files[0])} />
            </label>
          </div>
        ) : (
          <label className="flex aspect-[16/9] w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-slate-300 text-sm text-slate-500 hover:border-brand-400 hover:text-brand-600">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
            {uploading ? 'Uploading…' : 'Click to upload an image'}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploading}
              onChange={(e) => e.target.files?.[0] && handleFilePick(e.target.files[0])}
            />
          </label>
        )}
        {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
      </Field>

      <Field label="Link" hint="Opened when the banner is tapped — optional.">
        <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" />
      </Field>

      <Field label="Show to" required>
        <Select value={forSlider} onChange={(e) => setForSlider(e.target.value as SliderAudience)}>
          <option value="">Select audience</option>
          {SLIDER_AUDIENCES.map((a) => (
            <option key={a} value={a}>
              {AUDIENCE_LABEL[a]}
            </option>
          ))}
        </Select>
      </Field>

      {save.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(save.error)}</div>}

      <div className="flex justify-end gap-2 pt-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        <button className="btn-primary" disabled={!valid || save.isPending || uploading} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create'}
        </button>
      </div>
    </div>
  );
}
